/** @file requireDrawnAzimuths: refuses an entry drawing a tile only mirrored without drawnAzimuths. */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import { resolveSectorTable } from '@zilo/pose3d-format/tables/resolveSectorTable';
import type { FacetEntry } from '@zilo/pose3d-format/types/facet';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import { drawnAzimuthDefaults } from './drawnAzimuthDefaults';

/**
 * A `_FLIP` twin (and any table) that draws some tile only mirrored has no default drawn azimuth for it, which would
 * break `slots` later: VALIDATION at `<path>/drawnAzimuths` naming the table and tiles, with the suggested array and
 * `table_info`. An unknown table is left to the caller's own check.
 */
export function requireDrawnAzimuths(puppet: Pick<Puppet, 'sectorTables'>, entry: Pick<FacetEntry, 'yaw' | 'drawnAzimuths'>, path: string): void {
  if (entry.drawnAzimuths !== undefined) return;
  const table = resolveSectorTable(entry.yaw, puppet);
  if (table === undefined) return;
  const tiles = drawnAzimuthDefaults(table);
  const mirroredOnly = tiles.filter((t) => t.defaultDrawnAzimuth === null).map((t) => t.tile);
  if (mirroredOnly.length === 0) return;
  throwError(ERROR_CODES.VALIDATION, { table: entry.yaw, tiles: mirroredOnly }, {
    path: `${path}/drawnAzimuths`,
    hint: `${entry.yaw} draws tile(s) ${mirroredOnly.join(', ')} only mirrored, so the entry must say where each tile was drawn: `
      + `drawnAzimuths: [${tiles.map((t) => t.suggestedDrawnAzimuth).join(', ')}] (table_info { id: '${entry.yaw}' })`,
  });
}
